// src/utils/dueDateChange.js
//
// Pure state transform for editing an expense's due date. Used by the
// BudgetContext reducer so every entry point (Planner, Monthly, Annual)
// changes dates through exactly one code path:
//
//   1. the date is persisted on the authoritative Monthly/Annual record, and
//   2. any amount the Planner had placed in the OLD due week is moved to the
//      NEW due week (along with that week's paid/transferred flags), so no
//      duplicate occurrence is ever created and no planned money is lost.

import { getPlannerKey } from './plannerKeys';
import { parseDateParts, getWeekIndexForDate } from './recurrence';

/**
 * Validate a due-date input value. An empty string is allowed (it clears the
 * date); anything else must be a real calendar date in YYYY-MM-DD form.
 * @returns {{ ok: true }|{ ok: false, error: string }}
 */
export function validateDueDate(value) {
  if (value === '' || value === null || value === undefined) return { ok: true };
  const parts = parseDateParts(value);
  if (!parts) {
    return { ok: false, error: 'Enter a valid date (YYYY-MM-DD).' };
  }
  // Reject impossible days like Feb 30 that the regex alone can't catch.
  const roundTrip = new Date(parts.year, parts.month - 1, parts.day);
  if (roundTrip.getMonth() !== parts.month - 1 || roundTrip.getDate() !== parts.day) {
    return { ok: false, error: 'That day does not exist in the selected month.' };
  }
  return { ok: true };
}

/**
 * Move a planner entry's amount (and status flags) from one week bucket to
 * another. Returns a NEW entry; never mutates. Amounts are added into the
 * target week so manually planned money already there is preserved.
 */
export function movePlannerWeekAmount(entry, fromWeek, toWeek) {
  if (!entry || fromWeek === toWeek) return entry;
  if (fromWeek === null || toWeek === null) return entry;
  if (fromWeek < 0 || fromWeek > 4 || toWeek < 0 || toWeek > 4) return entry;

  const weeks = Array.isArray(entry.weeks) ? [...entry.weeks] : Array(5).fill(0);
  while (weeks.length < 5) weeks.push(0);
  const amount = parseFloat(weeks[fromWeek]) || 0;
  if (amount === 0) return entry;

  weeks[toWeek] = (parseFloat(weeks[toWeek]) || 0) + amount;
  weeks[fromWeek] = 0;

  const moveFlag = (flags) => {
    const arr = Array.isArray(flags) ? [...flags] : Array(5).fill(false);
    while (arr.length < 5) arr.push(false);
    arr[toWeek] = Boolean(arr[toWeek] || arr[fromWeek]);
    arr[fromWeek] = false;
    return arr;
  };

  return {
    ...entry,
    weeks,
    transferred: moveFlag(entry.transferred),
    paid: moveFlag(entry.paid)
  };
}

/**
 * Apply a due-date edit to the whole budget data object.
 *
 * @param {Object} data        state.data (never mutated).
 * @param {Object} descriptor  { collection: 'monthly'|'annual', categoryKey,
 *                               index, id, name } identifying the expense.
 * @param {string} newDate     "YYYY-MM-DD" or '' to clear.
 * @returns {{ data: Object, changed: boolean }}
 */
export function applyDueDateChange(data, descriptor, newDate) {
  const { collection, categoryKey } = descriptor || {};
  if (collection !== 'monthly' && collection !== 'annual') {
    return { data, changed: false };
  }

  const source = data?.[collection] || {};
  const list = Array.isArray(source[categoryKey]) ? source[categoryKey] : [];

  // Locate the expense: trust the index when it matches the id (or when no id
  // was supplied), otherwise fall back to an id search so a reordered list
  // can't corrupt a different row.
  let index = descriptor.index;
  let expense = list[index];
  if (descriptor.id && (!expense || (expense.id && expense.id !== descriptor.id))) {
    index = list.findIndex(exp => exp.id === descriptor.id);
    expense = index >= 0 ? list[index] : undefined;
  }
  if (!expense) return { data, changed: false };

  const oldDate = expense.date || '';
  const nextDate = newDate || '';
  if (oldDate === nextDate) return { data, changed: false };

  const updatedExpense = {
    ...expense,
    date: nextDate,
    id: expense.id || `${categoryKey}-${Date.now()}-${index}`
  };

  const updatedList = [...list];
  updatedList[index] = updatedExpense;

  const nextData = {
    ...data,
    [collection]: { ...source, [categoryKey]: updatedList }
  };

  // Re-home the Planner's week placement for this expense.
  const oldWeek = getWeekIndexForDate(oldDate);
  const newWeek = getWeekIndexForDate(nextDate);
  if (oldWeek !== null && newWeek !== null && oldWeek !== newWeek) {
    const plannerState = data.plannerState || {};
    const plannerKey = getPlannerKey({ ...updatedExpense, categoryKey });
    const legacyKey = updatedExpense.name;
    const entry = plannerState[plannerKey] || (legacyKey ? plannerState[legacyKey] : undefined);
    const moved = movePlannerWeekAmount(entry, oldWeek, newWeek);
    if (moved && moved !== entry) {
      const nextPlanner = { ...plannerState, [plannerKey]: moved };
      if (legacyKey && legacyKey !== plannerKey && plannerState[legacyKey]) {
        nextPlanner[legacyKey] = moved;
      }
      nextData.plannerState = nextPlanner;
    }
  }

  return { data: nextData, changed: true };
}
